import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCalendarAlt, FaTrash, FaCheck } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getApiUrl } from '../config/api';
import './NotificationsPage.css';

const extractMongoId = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const decoded = (() => {
      try {
        return decodeURIComponent(trimmed);
      } catch {
        return trimmed;
      }
    })();
    const matchObjectIdCtor = decoded.match(/ObjectId\(["']([a-fA-F0-9]{24})["']\)/);
    if (matchObjectIdCtor?.[1]) return matchObjectIdCtor[1];
    const matchRawHex = decoded.match(/^[a-fA-F0-9]{24}$/);
    return matchRawHex ? matchRawHex[0] : '';
  }

  if (typeof value === 'object') {
    if (value._id) return extractMongoId(value._id);
    if (value.$oid) return extractMongoId(value.$oid);
    if (value.id) return extractMongoId(value.id);
    if (typeof value.toString === 'function') {
      const raw = value.toString();
      if (raw && raw !== '[object Object]') {
        return extractMongoId(raw);
      }
    }
  }

  return '';
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserAndNotifications();

    const pollingInterval = setInterval(() => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        loadNotifications();
      }
    }, 20000);

    return () => clearInterval(pollingInterval);
  }, []);  const loadUserAndNotifications = () => {
    setLoading(true);
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        loadNotifications();
      } else {
        loadNotifications();
      }
    } catch (error) {
      console.error('Error loading user:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveNotificationUserType = () => {
    // Guest notifications page.
    // Host notifications are handled in Host dashboard (/host).
    return 'guest';
  };

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const userType = resolveNotificationUserType();

      if (!token) {
        const demoNotifications = createDemoNotifications();
        setNotifications(demoNotifications);
        updateNotificationCount(demoNotifications, userType);
        return;
      }

      const url = `${getApiUrl('/api/notifications/my-notifications')}?userType=${encodeURIComponent(userType)}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setNotifications([]);
        updateNotificationCount([], userType);
        return;
      }

      const result = await response.json();

      if (result.success && result.notifications) {
        const transformedNotifications = result.notifications.map((notif) => ({
          id: notif._id?.toString?.() ?? notif._id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          bookingId: extractMongoId(notif.bookingId),
          booking: typeof notif.bookingId === 'object' ? notif.bookingId : null,
          propertyId: extractMongoId(notif.propertyId),
          propertyName: notif.propertyName,
          actionUrl: notif.actionUrl || '',
          guestName: notif.guestName || notif.metadata?.guestName || '',
          companyName:
            notif.metadata?.companyName ||
            notif.metadata?.companySnapshot?.name ||
            '',
          staffDetails: notif.metadata?.staffDetails || null,
          metadata: notif.metadata || {},
          read: notif.read,
          createdAt: notif.createdAt
        }));

        setNotifications(transformedNotifications);
        updateNotificationCount(transformedNotifications, userType);

        // Auto mark-all-read when page is opened — reset badge to 0 immediately
        const hasUnread = transformedNotifications.some((n) => !n.read);
        if (hasUnread) {
          // Reset badge immediately in UI
          updateNotificationCount(transformedNotifications.map((n) => ({ ...n, read: true })), userType);
          setNotifications(transformedNotifications.map((n) => ({ ...n, read: true })));
          // Call API in background
          fetch(getApiUrl('/api/notifications/mark-all-read'), {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ userType })
          }).catch(() => {});
        }
      } else {
        const demoNotifications = createDemoNotifications();
        setNotifications(demoNotifications);
        updateNotificationCount(demoNotifications, userType);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      const demoNotifications = createDemoNotifications();
      setNotifications(demoNotifications);
      updateNotificationCount(demoNotifications, resolveNotificationUserType());
    }
  };

  const createDemoNotifications = () => {
    return [
      {
        id: `notif_${Date.now()}_1`,
        type: 'meeting_room_booked',
        title: 'New Meeting Room Booking',
        message: 'SAFIYA Safiya booked Lotus Executive Boardroom for tomorrow morning.',
        bookingId: '69cba471947de09918635369',
        propertyName: 'Lotus Executive Boardroom',
        guestName: 'SAFIYA Safiya',
        actionUrl: '/meetings/my-bookings',
        metadata: { bookingType: 'meeting' },
        read: false,
        createdAt: new Date().toISOString()
      },
      {
        id: `notif_${Date.now()}_2`,
        type: 'meeting_room_booked',
        title: 'New Meeting Room Booking',
        message: 'Arun Kumar booked Zenith Conference Hall for a product sync.',
        bookingId: '69cb6b8922b292c67a13c0fa',
        propertyName: 'Zenith Conference Hall',
        guestName: 'Arun Kumar',
        actionUrl: '/meetings/my-bookings',
        metadata: { bookingType: 'meeting' },
        read: false,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
      },
      {
        id: `notif_${Date.now()}_3`,
        type: 'booking_confirmed',
        title: 'Booking Confirmed',
        message: 'Your booking for Orchid Hotel has been confirmed.',
        bookingId: 'BK12345679',
        propertyName: 'Orchid Hotel',
        actionUrl: '/booking-details/BK12345679',
        metadata: { bookingType: 'stay' },
        read: true,
        createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
      }
    ];
  };

  const markAsRead = async (notificationId) => {
    if (!notificationId) return;

    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(getApiUrl(`/api/notifications/${notificationId}/read`), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    const updated = notifications.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    setNotifications(updated);
    updateNotificationCount(updated);
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const userType = resolveNotificationUserType();
      if (token) {
        await fetch(getApiUrl('/api/notifications/mark-all-read'), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ userType })
        });
      }

      const updated = notifications.map((n) => ({ ...n, read: true }));
      setNotifications(updated);
      updateNotificationCount(updated, userType);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const userType = resolveNotificationUserType();
      if (token) {
        await fetch(`${getApiUrl('/api/notifications/clear-all')}?userType=${encodeURIComponent(userType)}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }

      setNotifications([]);
      updateNotificationCount([], userType);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  const updateNotificationCount = (notificationsList, userType = 'guest') => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const userId = user._id || user.id;
        const unreadCount = notificationsList.filter((n) => !n.read).length;
        localStorage.setItem(`notificationCount_${userType}_${userId}`, unreadCount.toString());
        window.dispatchEvent(new CustomEvent('notificationBadgeUpdate', { detail: { unreadCount, userType } }));
      }
    } catch (error) {
      console.error('Error updating notification count:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const bookingType = notification.metadata?.bookingType;
    const bookingId = extractMongoId(notification.bookingId);
    const propertyId = extractMongoId(notification.propertyId);
    const actionUrl = String(notification.actionUrl || '').trim();

    if (actionUrl.startsWith('/booking-details/')) {
      const pathBookingId = extractMongoId(actionUrl.split('/booking-details/')[1] || '');
      if (pathBookingId) {
        navigate(`/booking-details/${pathBookingId}`);
        return;
      }
      if (bookingId) {
        navigate(`/booking-details/${bookingId}`);
        return;
      }
      navigate('/trips');
      return;
    }

    if (actionUrl && actionUrl.startsWith('/')) {
      navigate(actionUrl);
      return;
    }

    if (bookingType === 'meeting' || notification.type === 'meeting_room_booked') {
      navigate('/meetings/my-bookings');
      return;
    }

    if (bookingId) {
      navigate(`/booking-details/${bookingId}`);
      return;
    }

    if (propertyId) {
      navigate(`/property/${propertyId}`);
      return;
    }

    navigate('/trips');
  };

  const getBookedByLabel = (notification) => {
    const staffName = String(notification?.staffDetails?.name || '').trim();
    const role = String(notification?.staffDetails?.role || '').trim();
    if (!staffName) return '';
    return role ? `${staffName} (${role})` : staffName;
  };

  if (loading) {
    return (
      <div className="notifications-page-modern">
        <Navbar />
        <div className="notifications-shell">
          <div className="notifications-loading">Loading notifications...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page-modern">
      <Navbar />

      <main className="notifications-shell">
        <header className="notifications-head">
          <div>
            <h1>Notifications</h1>
            <p>Stay updated with your property activities</p>
          </div>
          <div className="notif-head-actions">
            <button className="btn-clear-all" onClick={clearAllNotifications}>
              <FaTrash /> Clear all
            </button>
            <button className="btn-mark-read" onClick={markAllAsRead}>
              <FaCheck /> Mark all as read
            </button>
          </div>
        </header>

        {notifications.length === 0 ? (
          <div className="notif-empty-state">
            <FaBell />
            <h3>No notifications yet</h3>
            <p>You'll see notifications here when there are updates.</p>
          </div>
        ) : (
          <section className="notif-cards-list">
            {notifications.map((notification) => {
              const itemName = notification.propertyName || 'Booking';
              const title = notification.title || 'Notification';
              const bookedByLabel = getBookedByLabel(notification);

              return (
                <article
                  key={notification.id}
                  className={`meeting-notif-card ${notification.read ? '' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="meeting-icon-circle">
                    {notification.type === 'meeting_room_booked' ? <FaCalendarAlt /> : <FaBell />}
                  </div>

                  <div className="meeting-card-body">
                    <div className="meeting-card-head-row">
                      <h3>{title}</h3>
                      <span className="meeting-card-date">{formatDate(notification.createdAt)}</span>
                    </div>

                    <p className="meeting-card-desc">{notification.message}</p>

                    <div className="meeting-card-meta">
                      <span className="meta-pill">{itemName}</span>
                      {notification.companyName && (
                        <span className="meta-pill">Company: {notification.companyName}</span>
                      )}
                      {bookedByLabel && (
                        <span className="meta-pill">Booked By: {bookedByLabel}</span>
                      )}
                      {notification.staffDetails?.employeeId && (
                        <span className="meta-pill">Employee ID: {notification.staffDetails.employeeId}</span>
                      )}
                      <span className="meta-pill booking-id-pill">
                        Booking #{String(extractMongoId(notification.bookingId) || '').slice(-16) || 'N/A'}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
